import { BranchFormProps } from "../types/branch";
import { removeEmojis, parseDate } from "../utils/validation";
import { useLocationOptions } from "../hooks/useLocationOptions";
import React from "react";

export const useBranchFields = ({
  formData,
  doberror,
  error,
  refs,
  onFormChange,
  onDobChange,
  onPincodeChange,
  onBranchnameKeyPress,
  createKeyPressHandler,
  onKeyPress,
  isEdit = false,
  disabledFields = [],
}: BranchFormProps) => {
  const { countries, states, cities } = useLocationOptions(
    formData.selectedCountry,
    formData.selectedState
  );

  /** Utility: check if field should be disabled */
  const isDisabled = (field: string) =>
    disabledFields.includes(field) || (isEdit && field === "Branchname");

  /** Map handlers per field */
  const fieldHandlers: Record<string, (value: string) => void> = {
    Branchname: (value) => onFormChange({ Branchname: removeEmojis(value) }),
    address1: (value) => onFormChange({ address1: removeEmojis(value) }),
    street: (value) => onFormChange({ street: removeEmojis(value) }),
    branchopeningdate: (value) => {
      const cleanValue = removeEmojis(value).trim();
      const parsed = parseDate(cleanValue);

      onDobChange({
        target: { value: cleanValue },
      } as React.ChangeEvent<HTMLInputElement>);

      if (refs?.setDobError) {
        refs.setDobError(!parsed);
      }
    },
    Pincode: (value) =>
      onPincodeChange({
        target: { value },
      } as React.ChangeEvent<HTMLInputElement>),
    Country: (value) =>
      onFormChange({ selectedCountry: value, selectedState: "", selectedCity: "" }),
    State: (value) => onFormChange({ selectedState: value, selectedCity: "" }),
    City: (value) => onFormChange({ selectedCity: value }),
  };

  /** JSX-ready required label */
  const RequiredLabel: React.FC<{ label: string }> = ({ label }) => (
    <>
      {label} <span className="text-red-500">*</span>
    </>
  );

  /** Generic change handler */
  const handleChange = (field: string, value: string) => {
    fieldHandlers[field]?.(value);
  };

  /** Define all fields in one place */
  const fields = [
    {
      id: "Branchname",
      label: <RequiredLabel label="Branch name" />,
      value: formData.Branchname,
      keyPress: onBranchnameKeyPress,
      ref: undefined,
      disabled: isDisabled("Branchname"),
      type: "text" as const,
    },
    {
      id: "branchopeningdate",
      label: "Branch opening date (dd-mm-yyyy)",
      value: formData.inputValue || formData.branchopeningdate || "",
      ref: refs.branchopeningdateref,
      keyPress: createKeyPressHandler(refs.addressline1),
      error: !!doberror,
      helperText: doberror ? "Invalid date format, use dd-mm-yyyy." : undefined,
      disabled: isDisabled("branchopeningdate"),
      type: "text" as const,
      onChange: (val: string) => handleChange("branchopeningdate", val),
      onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!/[0-9-]/.test(e.key)) e.preventDefault();
      },
    },
    {
      id: "address1",
      label: "Flat /House no./Building /Apartment",
      value: formData.address1,
      ref: refs.addressline1,
      keyPress: createKeyPressHandler(refs.streetnames),
      disabled: isDisabled("address1"),
      type: "text" as const,
    },
    {
      id: "street",
      label: "Area /Street /Sector /Town",
      value: formData.street,
      ref: refs.streetnames,
      keyPress: createKeyPressHandler(refs.Countryref),
      disabled: isDisabled("street"),
      type: "text" as const,
    },
    {
      id: "Country",
      label: "Country",
      value: formData.selectedCountry,
      ref: refs.Countryref,
      keyPress: createKeyPressHandler(refs.Stateref),
      type: "select" as const,
      options: countries,
      grid: 4,
      disabled: isDisabled("selectedCountry"),
    },
    {
      id: "State",
      label: "State",
      value: formData.selectedState,
      ref: refs.Stateref,
      keyPress: createKeyPressHandler(refs.Cityref),
      type: "select" as const,
      options: states,
      grid: 4,
      disabled: !formData.selectedCountry || isDisabled("selectedState"),
    },
    {
      id: "City",
      label: "City",
      value: formData.selectedCity,
      ref: refs.Cityref,
      keyPress: createKeyPressHandler(refs.pincoderef),
      type: "select" as const,
      options: cities,
      grid: 4,
      disabled: !formData.selectedState || isDisabled("selectedCity"),
    },
    {
      id: "Pincode",
      label: "Pincode/Zipcode",
      value: formData.Pincode,
      ref: refs.pincoderef,
      keyPress: onKeyPress || createKeyPressHandler(refs.pincoderef),
      error: !!error,
      helperText: error || undefined,
      disabled: isDisabled("Pincode"),
      type: "text" as const,
    },
  ];

  return { fields, handleChange };
};
